import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import {
  Sstv2DlqMessage,
  Sstv2DlqQueueRow,
  useGetSstv2DlqMessagesQuery,
  useGetSstv2DlqQueuesQuery,
} from "src/features/partners/partnersApi"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatSentTime = (iso: string | null): string => {
  if (!iso) return "-"
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

const prettyBody = (rawBody: string): string => {
  try {
    return JSON.stringify(JSON.parse(rawBody), null, 2)
  } catch {
    return rawBody
  }
}

// A muted depth badge. DLQ depth > 0 is a problem, so it reads "error" (red);
// everything else stays neutral/primary — no yellow/amber fills (house style).
const DepthChip: FC<{ depth: number | null; isDlq?: boolean }> = ({
  depth,
  isDlq,
}) => {
  if (depth == null) {
    return (
      <Chip label="n/a" size="small" variant="outlined" color="default" />
    )
  }
  const color: "error" | "primary" | "default" =
    isDlq && depth > 0 ? "error" : depth > 0 ? "primary" : "default"
  return (
    <Chip
      label={depth}
      size="small"
      variant={depth > 0 ? "filled" : "outlined"}
      color={color}
    />
  )
}

// ---------------------------------------------------------------------------
// Message row (expandable raw body)
// ---------------------------------------------------------------------------

const DlqMessageRow: FC<{ message: Sstv2DlqMessage }> = ({ message }) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)}>
            {open ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{message.event || "-"}</TableCell>
        <TableCell>
          <Typography variant="caption" fontFamily="monospace">
            {message.sl1LockerId || "-"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption" fontFamily="monospace">
            {message.sl2LockerId || "-"}
          </Typography>
        </TableCell>
        <TableCell align="center">{message.receiveCount ?? "-"}</TableCell>
        <TableCell>{formatSentTime(message.sentTime)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: "none" }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              component="pre"
              sx={{
                m: 1,
                p: 1.5,
                bgcolor: "rgba(35, 66, 133, 0.04)",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: "0.72rem",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              {prettyBody(message.rawBody)}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

// ---------------------------------------------------------------------------
// DLQ messages table (fetches when mounted, i.e. when the queue is expanded)
// ---------------------------------------------------------------------------

const Sstv2DlqMessagesTable: FC<{ dlqUrl: string }> = ({ dlqUrl }) => {
  const q = useGetSstv2DlqMessagesQuery({ queueUrl: dlqUrl })
  const messages = q.data?.data ?? []

  if (q.isLoading || q.isFetching) {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1.5 }}>
        <CircularProgress size={16} />
        <Typography variant="caption">Loading messages…</Typography>
      </Stack>
    )
  }

  if (q.isError) {
    return (
      <Typography variant="caption" color="error" sx={{ py: 1.5 }}>
        Failed to load DLQ messages.
      </Typography>
    )
  }

  if (messages.length === 0) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ py: 1.5 }}
      >
        No messages in this DLQ (showing up to 10; peek is non-destructive).
      </Typography>
    )
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>
              <strong>Event</strong>
            </TableCell>
            <TableCell>
              <strong>SL1 Locker</strong>
            </TableCell>
            <TableCell>
              <strong>SL2 Locker</strong>
            </TableCell>
            <TableCell align="center">
              <strong>Receive #</strong>
            </TableCell>
            <TableCell>
              <strong>Sent</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {messages.map((m, i) => (
            <DlqMessageRow key={m.messageId ?? i} message={m} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// ---------------------------------------------------------------------------
// Queue row (expandable → DLQ messages)
// ---------------------------------------------------------------------------

const DlqQueueRowView: FC<{ row: Sstv2DlqQueueRow }> = ({ row }) => {
  const [open, setOpen] = useState(false)
  const canExpand = !!row.dlqUrl
  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <Tooltip title={canExpand ? "Show DLQ messages" : "No DLQ"}>
            <span>
              <IconButton
                size="small"
                disabled={!canExpand}
                onClick={() => setOpen((o) => !o)}
              >
                {open ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Typography variant="caption" fontWeight="bold">
            {row.configTag}
          </Typography>
        </TableCell>
        <TableCell>{row.configName}</TableCell>
        <TableCell>
          <Typography variant="caption" fontFamily="monospace">
            {row.queueName}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <DepthChip depth={row.mainDepth} />
        </TableCell>
        <TableCell>
          <Typography variant="caption" fontFamily="monospace">
            {row.dlqName || "-"}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <DepthChip depth={row.dlqDepth} isDlq />
        </TableCell>
      </TableRow>
      {canExpand && (
        <TableRow>
          <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Sstv2DlqMessagesTable dlqUrl={row.dlqUrl as string} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const SaSstv2DlqManagement: FC = () => {
  const q = useGetSstv2DlqQueuesQuery()
  const rows = q.data?.data ?? []
  const isLoading = q.isLoading || q.isFetching

  return (
    <Stack data-ui="UI-SL2W-245" direction="column" spacing={2} width="100%">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          {`DLQ Viewer (${rows.length})`}
        </Typography>
        {isLoading && <CircularProgress size={18} />}
        <div style={{ flex: 1 }} />
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={() => q.refetch()}
              disabled={isLoading}
              sx={{
                bgcolor: "rgba(35, 66, 133, 0.06)",
                color: "primary.main",
                "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="caption" color="textSecondary">
        Read-only view of SST V2 SQS queues and their dead-letter queues.
        Expand a queue to peek at up to 10 DLQ messages (non-destructive — no
        purge or re-drive here).
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>
                <strong>Tag</strong>
              </TableCell>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>Queue</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Main Depth</strong>
              </TableCell>
              <TableCell>
                <strong>DLQ</strong>
              </TableCell>
              <TableCell align="center">
                <strong>DLQ Depth</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <DlqQueueRowView key={`${row.configTag}-${i}`} row={row} />
            ))}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ py: 3 }}
                  >
                    No SST V2 queues found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default SaSstv2DlqManagement
